import {
  clonePlainData,
  isPlainDataEqual,
  isPlainObject,
} from './object.ts';
import {
  matchSequences,
} from './sequence-diff.ts';

/**
 * A property name for objects; for arrays the item key, or the index when the array has no key.
 */
export type TDiffPathSegment = string | number;
export type TDiffPath = readonly TDiffPathSegment[];

export type TThreeWayVersion = 'original' | 'latest' | 'edited';

export const THREE_WAY_VERSIONS: readonly TThreeWayVersion[] = [
  'original',
  'latest',
  'edited',
];

/**
 * The version to keep. `combined` keeps the items of the latest and of the edited version, which is
 * only possible for list segments where both versions inserted items at the same place.
 */
export type TThreeWayChoice = TThreeWayVersion | 'combined';

export const THREE_WAY_CHOICES: readonly TThreeWayChoice[] = [
  ...THREE_WAY_VERSIONS,
  'combined',
];

export interface IThreeWayVersions<T> {
  /** The value when editing started. */
  original: T,
  /** The value fetched again just before saving. */
  latest: T,
  /** The original value with the user's changes. */
  edited: T,
}

interface IPresentDiffEntry {
  isPresent: true,
  value: unknown,
}

interface IAbsentDiffEntry {
  isPresent: false,
}

/** The value of one version at a path. Absent means the property or array item does not exist. */
export type TDiffEntry = IPresentDiffEntry | IAbsentDiffEntry;

/**
 * - `changedInLatest`: only the latest version differs from the original.
 * - `changedInBoth`: the latest and the edited version both differ from the original, and from each other.
 */
export type TThreeWayDiffRowKind = 'changedInLatest' | 'changedInBoth';

export interface IIndexRange {
  start: number,
  /** Exclusive. */
  end: number,
}

/** A part of a list that the versions changed differently, e.g. items inserted at the same place. */
export interface IThreeWaySegment {
  /** Where the segment is in the list of each version. */
  ranges: Record<TThreeWayVersion, IIndexRange>,
  /** Both versions only inserted items here, so the items of both can be kept. */
  canCombine: boolean,
}

export interface IThreeWayDiffRow {
  /** Unique within one diff, stable for the same versions. */
  id: string,
  /**
   * Identifies the value across the versions: property names, the key of keyed array items and,
   * for items of other lists, their index in the original. Match it with {@link matchesPathPattern}.
   */
  path: TDiffPath,
  /**
   * The property names and indices that lead to the value in each version, `undefined` where the
   * version does not have it. For a list segment, the path of the list.
   */
  versionPaths: Record<TThreeWayVersion, TDiffPath | undefined>,
  kind: TThreeWayDiffRowKind,
  /** For a list segment, the items of the segment in this version, as an array. */
  original: TDiffEntry,
  latest: TDiffEntry,
  edited: TDiffEntry,
  /** Set when the row is a part of a list instead of a single value. */
  segment?: IThreeWaySegment,
}

export type TArrayItemKeyGetter = (item: unknown) => TDiffPathSegment;

export interface IThreeWayDiffOptions {
  /** Returns how the items of the array at `arrayPath` are matched across the versions. */
  getArrayItemKey?: (arrayPath: TDiffPath) => TArrayItemKeyGetter | undefined,
  /**
   * Match the items of the array at `arrayPath` by their index, like the values of a fixed-size
   * vector. When the versions differ in length, the whole array becomes a single row.
   *
   * Arrays matched neither by key nor by index are merged as lists: insertions, removals and
   * changes in different places of the list are combined, and only places that both versions
   * changed differently become rows.
   */
  matchArrayItemsByIndex?: (arrayPath: TDiffPath) => boolean,
  /** Compare the value at `path` as a whole, so it becomes a single row instead of one per field. */
  isAtomic?: (path: TDiffPath, versions: IThreeWayVersions<unknown>) => boolean,
  /** Leave the value at `path` out of the comparison. The edited value is kept. */
  ignorePath?: (path: TDiffPath) => boolean,
}

/** The chosen version per row id. Rows without a choice use {@link getDefaultThreeWayChoice}. */
export type TThreeWayChoices = Record<string, TThreeWayChoice | undefined>;

export interface IThreeWayMergeResult<T> {
  rows: IThreeWayDiffRow[],
  /** The edited value with the choices applied; unresolved rows keep the edited value. */
  merged: T,
  /** Rows that changed in both versions and have no choice yet. */
  unresolvedRowIds: string[],
}

/** Matches any single segment in {@link matchesPathPattern}. */
export const ANY_PATH_SEGMENT = '*';

const ABSENT_ENTRY: TDiffEntry = {
  isPresent: false,
};

type TVersionPaths = Record<TThreeWayVersion, TDiffPath | undefined>;

interface INodeLocation {
  path: TDiffPath,
  versionPaths: TVersionPaths,
}

interface IMergeContext {
  options: IThreeWayDiffOptions,
  choices: TThreeWayChoices,
  rows: IThreeWayDiffRow[],
  unresolvedRowIds: string[],
}

interface IChildNode {
  location: INodeLocation,
  entries: IThreeWayVersions<TDiffEntry>,
}

function toPresentEntry(entryValue: unknown): TDiffEntry {
  return {
    isPresent: true,
    value: entryValue,
  };
}

function toPropertyEntry(propertyValue: unknown): TDiffEntry {
  return propertyValue === undefined
    ? ABSENT_ENTRY
    : toPresentEntry(propertyValue);
}

function appendToPath(path: TDiffPath | undefined, segment: TDiffPathSegment): TDiffPath | undefined {
  return path && [
    ...path,
    segment,
  ];
}

function getUniqueOrderedKeys<KEY>(keyLists: KEY[][]): KEY[] {
  return [
    ...new Set(keyLists.flat()),
  ];
}

function mapItemsByKey(items: unknown[], getItemKey: TArrayItemKeyGetter): Map<TDiffPathSegment, unknown> | undefined {
  const itemsByKey = new Map<TDiffPathSegment, unknown>();
  for (const item of items) {
    const itemKey = getItemKey(item);
    if (itemsByKey.has(itemKey)) {
      return undefined;
    }
    itemsByKey.set(itemKey, item);
  }
  return itemsByKey;
}

/** Deep equality that skips the paths ignored by the options and matches keyed array items by key. */
function areValuesEqual(path: TDiffPath, first: unknown, second: unknown, options: IThreeWayDiffOptions): boolean {
  if (options.ignorePath?.(path)) {
    return true;
  }
  if (isPlainObject(first) && isPlainObject(second)) {
    const propertyNames = getUniqueOrderedKeys([
      Object.keys(first),
      Object.keys(second),
    ]);
    return propertyNames.every((propertyName) => areValuesEqual([
      ...path,
      propertyName,
    ], first[propertyName], second[propertyName], options));
  }
  if (Array.isArray(first) && Array.isArray(second)) {
    if (first.length !== second.length) {
      return false;
    }
    const getItemKey = options.getArrayItemKey?.(path);
    if (!getItemKey) {
      return first.every((item, index) => areValuesEqual([
        ...path,
        index,
      ], item, second[index], options));
    }
    const firstItemsByKey = mapItemsByKey(first, getItemKey);
    const secondItemsByKey = mapItemsByKey(second, getItemKey);
    if (!firstItemsByKey || !secondItemsByKey) {
      return isPlainDataEqual(first, second);
    }
    return [
      ...firstItemsByKey,
    ].every(([
      itemKey,
      item,
    ]) => secondItemsByKey.has(itemKey) && areValuesEqual([
      ...path,
      itemKey,
    ], item, secondItemsByKey.get(itemKey), options));
  }
  return isPlainDataEqual(first, second);
}

function areEntriesEqual(path: TDiffPath, firstEntry: TDiffEntry, secondEntry: TDiffEntry, options: IThreeWayDiffOptions): boolean {
  if (!firstEntry.isPresent || !secondEntry.isPresent) {
    return firstEntry.isPresent === secondEntry.isPresent;
  }
  return areValuesEqual(path, firstEntry.value, secondEntry.value, options);
}

function getPresentValues(entries: IThreeWayVersions<TDiffEntry>): IThreeWayVersions<unknown> | undefined {
  if (!entries.original.isPresent || !entries.latest.isPresent || !entries.edited.isPresent) {
    return undefined;
  }
  return {
    original: entries.original.value,
    latest: entries.latest.value,
    edited: entries.edited.value,
  };
}

function getRowKind(path: TDiffPath, entries: IThreeWayVersions<TDiffEntry>, options: IThreeWayDiffOptions): TThreeWayDiffRowKind {
  return areEntriesEqual(path, entries.original, entries.edited, options)
    ? 'changedInLatest'
    : 'changedInBoth';
}

/** Records the row and returns the version chosen for it. */
function resolveRow(row: IThreeWayDiffRow, context: IMergeContext): TThreeWayChoice {
  context.rows.push(row);
  const choice = context.choices[row.id] ?? getDefaultThreeWayChoice(row);
  if (choice === undefined) {
    context.unresolvedRowIds.push(row.id);
    return 'edited';
  }
  if (choice === 'combined' && !row.segment?.canCombine) {
    throw new Error(`The versions of ${row.id} cannot be combined.`);
  }
  return choice;
}

function mergeChildren(children: IChildNode[], context: IMergeContext): TDiffEntry[] {
  return children.map((child) => mergeNode(child.location, child.entries, context));
}

function mergeObject(location: INodeLocation, objects: IThreeWayVersions<Record<string, unknown>>, context: IMergeContext): TDiffEntry {
  const propertyNames = getUniqueOrderedKeys([
    Object.keys(objects.edited),
    Object.keys(objects.latest),
    Object.keys(objects.original),
  ]);
  const mergedEntries = mergeChildren(propertyNames.map((propertyName) => ({
    location: {
      path: [
        ...location.path,
        propertyName,
      ],
      versionPaths: {
        original: appendToPath(location.versionPaths.original, propertyName),
        latest: appendToPath(location.versionPaths.latest, propertyName),
        edited: appendToPath(location.versionPaths.edited, propertyName),
      },
    },
    entries: {
      original: toPropertyEntry(objects.original[propertyName]),
      latest: toPropertyEntry(objects.latest[propertyName]),
      edited: toPropertyEntry(objects.edited[propertyName]),
    },
  })), context);

  const merged: Record<string, unknown> = {};
  propertyNames.forEach((propertyName, index) => {
    const mergedEntry = mergedEntries[index];
    if (mergedEntry.isPresent) {
      merged[propertyName] = mergedEntry.value;
    }
  });
  return toPresentEntry(merged);
}

function getPresentItem(entry: TDiffEntry, path: TDiffPath): unknown {
  if (!entry.isPresent) {
    throw new Error(`The item at ${JSON.stringify(path)} exists in every version but was merged as missing.`);
  }
  return entry.value;
}

function mergeIndexedArray(location: INodeLocation, arrays: IThreeWayVersions<unknown[]>, context: IMergeContext): TDiffEntry | undefined {
  const itemCount = arrays.original.length;
  if (arrays.latest.length !== itemCount || arrays.edited.length !== itemCount) {
    return undefined;
  }
  const mergedEntries = mergeChildren(arrays.original.map((originalItem, index) => ({
    location: {
      path: [
        ...location.path,
        index,
      ],
      versionPaths: {
        original: appendToPath(location.versionPaths.original, index),
        latest: appendToPath(location.versionPaths.latest, index),
        edited: appendToPath(location.versionPaths.edited, index),
      },
    },
    entries: {
      original: toPresentEntry(originalItem),
      latest: toPresentEntry(arrays.latest[index]),
      edited: toPresentEntry(arrays.edited[index]),
    },
  })), context);
  return toPresentEntry(mergedEntries.map((mergedEntry, index) => getPresentItem(mergedEntry, [
    ...location.path,
    index,
  ])));
}

/** Inserts after the closest item that precedes the new item in its source list, or at the start. */
function insertKeyInOrder(orderedKeys: TDiffPathSegment[], sourceKeys: TDiffPathSegment[], itemKey: TDiffPathSegment) {
  const sourceIndex = sourceKeys.indexOf(itemKey);
  for (let i = sourceIndex - 1; i >= 0; i--) {
    const precedingIndex = orderedKeys.indexOf(sourceKeys[i]);
    if (precedingIndex >= 0) {
      orderedKeys.splice(precedingIndex + 1, 0, itemKey);
      return;
    }
  }
  orderedKeys.unshift(itemKey);
}

function mergeKeyedArray(
  location: INodeLocation,
  arrays: IThreeWayVersions<unknown[]>,
  getItemKey: TArrayItemKeyGetter,
  context: IMergeContext,
): TDiffEntry | undefined {
  const originalItemsByKey = mapItemsByKey(arrays.original, getItemKey);
  const latestItemsByKey = mapItemsByKey(arrays.latest, getItemKey);
  const editedItemsByKey = mapItemsByKey(arrays.edited, getItemKey);
  if (!originalItemsByKey || !latestItemsByKey || !editedItemsByKey) {
    return undefined;
  }
  const keysByVersion: Record<TThreeWayVersion, TDiffPathSegment[]> = {
    original: [
      ...originalItemsByKey.keys(),
    ],
    latest: [
      ...latestItemsByKey.keys(),
    ],
    edited: [
      ...editedItemsByKey.keys(),
    ],
  };

  function toItemEntry(itemsByKey: Map<TDiffPathSegment, unknown>, itemKey: TDiffPathSegment): TDiffEntry {
    return itemsByKey.has(itemKey)
      ? toPresentEntry(itemsByKey.get(itemKey))
      : ABSENT_ENTRY;
  }

  function toItemVersionPath(version: TThreeWayVersion, itemKey: TDiffPathSegment): TDiffPath | undefined {
    const index = keysByVersion[version].indexOf(itemKey);
    return index >= 0
      ? appendToPath(location.versionPaths[version], index)
      : undefined;
  }

  const itemKeys = getUniqueOrderedKeys([
    keysByVersion.edited,
    keysByVersion.latest,
    keysByVersion.original,
  ]);
  const mergedEntries = mergeChildren(itemKeys.map((itemKey) => ({
    location: {
      path: [
        ...location.path,
        itemKey,
      ],
      versionPaths: {
        original: toItemVersionPath('original', itemKey),
        latest: toItemVersionPath('latest', itemKey),
        edited: toItemVersionPath('edited', itemKey),
      },
    },
    entries: {
      original: toItemEntry(originalItemsByKey, itemKey),
      latest: toItemEntry(latestItemsByKey, itemKey),
      edited: toItemEntry(editedItemsByKey, itemKey),
    },
  })), context);

  const mergedItemsByKey = new Map<TDiffPathSegment, unknown>();
  itemKeys.forEach((itemKey, index) => {
    const mergedEntry = mergedEntries[index];
    if (mergedEntry.isPresent) {
      mergedItemsByKey.set(itemKey, mergedEntry.value);
    }
  });
  const orderedKeys = keysByVersion.edited.filter((itemKey) => mergedItemsByKey.has(itemKey));
  [
    ...mergedItemsByKey.keys(),
  ]
    .filter((itemKey) => !editedItemsByKey.has(itemKey))
    .forEach((itemKey) => {
      const sourceKeys = latestItemsByKey.has(itemKey)
        ? keysByVersion.latest
        : keysByVersion.original;
      insertKeyInOrder(orderedKeys, sourceKeys, itemKey);
    });
  return toPresentEntry(orderedKeys.map((itemKey) => mergedItemsByKey.get(itemKey)));
}

function sliceRanges(arrays: IThreeWayVersions<unknown[]>, ranges: Record<TThreeWayVersion, IIndexRange>): IThreeWayVersions<unknown[]> {
  return {
    original: arrays.original.slice(ranges.original.start, ranges.original.end),
    latest: arrays.latest.slice(ranges.latest.start, ranges.latest.end),
    edited: arrays.edited.slice(ranges.edited.start, ranges.edited.end),
  };
}

function mergeListSegment(
  location: INodeLocation,
  arrays: IThreeWayVersions<unknown[]>,
  ranges: Record<TThreeWayVersion, IIndexRange>,
  context: IMergeContext,
): unknown[] {
  const segments = sliceRanges(arrays, ranges);
  const originalStart = ranges.original.start;

  function areSegmentsEqual(firstSegment: unknown[], secondSegment: unknown[]): boolean {
    return firstSegment.length === secondSegment.length
      && firstSegment.every((item, offset) => areValuesEqual([
        ...location.path,
        originalStart + offset,
      ], item, secondSegment[offset], context.options));
  }

  if (areSegmentsEqual(segments.original, segments.latest) || areSegmentsEqual(segments.latest, segments.edited)) {
    return segments.edited;
  }

  const isChangedInLatestOnly = areSegmentsEqual(segments.original, segments.edited);
  const canPairItems = segments.original.length === segments.latest.length
    && segments.original.length === segments.edited.length;
  if (canPairItems) {
    const mergedEntries = mergeChildren(segments.original.map((originalItem, offset) => ({
      location: {
        path: [
          ...location.path,
          originalStart + offset,
        ],
        versionPaths: {
          original: appendToPath(location.versionPaths.original, originalStart + offset),
          latest: appendToPath(location.versionPaths.latest, ranges.latest.start + offset),
          edited: appendToPath(location.versionPaths.edited, ranges.edited.start + offset),
        },
      },
      entries: {
        original: toPresentEntry(originalItem),
        latest: toPresentEntry(segments.latest[offset]),
        edited: toPresentEntry(segments.edited[offset]),
      },
    })), context);
    return mergedEntries.map((mergedEntry, offset) => getPresentItem(mergedEntry, [
      ...location.path,
      originalStart + offset,
    ]));
  }

  const segmentPath = [
    ...location.path,
    originalStart,
  ];
  const choice = resolveRow({
    id: `${JSON.stringify(segmentPath)}:segment`,
    path: segmentPath,
    versionPaths: location.versionPaths,
    kind: isChangedInLatestOnly ? 'changedInLatest' : 'changedInBoth',
    original: toPresentEntry(segments.original),
    latest: toPresentEntry(segments.latest),
    edited: toPresentEntry(segments.edited),
    segment: {
      ranges,
      canCombine: !isChangedInLatestOnly && segments.original.length === 0,
    },
  }, context);
  return choice === 'combined'
    ? [
      ...segments.latest,
      ...segments.edited,
    ]
    : segments[choice];
}

interface IListAnchor {
  original: number,
  latest: number,
  edited: number,
}

/**
 * Merges like a three-way text merge: items that are unchanged in both versions anchor the list,
 * and each part between two anchors is merged on its own.
 */
function mergeList(location: INodeLocation, arrays: IThreeWayVersions<unknown[]>, context: IMergeContext): TDiffEntry {
  function areItemsEqual(originalItem: unknown, otherItem: unknown, originalIndex: number): boolean {
    return areValuesEqual([
      ...location.path,
      originalIndex,
    ], originalItem, otherItem, context.options);
  }

  const latestIndexByOriginalIndex = new Map(matchSequences(arrays.original, arrays.latest, areItemsEqual)
    .map((match) => [
      match.firstIndex,
      match.secondIndex,
    ]));
  const editedIndexByOriginalIndex = new Map(matchSequences(arrays.original, arrays.edited, areItemsEqual)
    .map((match) => [
      match.firstIndex,
      match.secondIndex,
    ]));

  const anchors: IListAnchor[] = [];
  arrays.original.forEach((_originalItem, originalIndex) => {
    const latestIndex = latestIndexByOriginalIndex.get(originalIndex);
    const editedIndex = editedIndexByOriginalIndex.get(originalIndex);
    if (latestIndex !== undefined && editedIndex !== undefined) {
      anchors.push({
        original: originalIndex,
        latest: latestIndex,
        edited: editedIndex,
      });
    }
  });
  const endAnchor: IListAnchor = {
    original: arrays.original.length,
    latest: arrays.latest.length,
    edited: arrays.edited.length,
  };

  const merged: unknown[] = [];
  let previousAnchor: IListAnchor = {
    original: -1,
    latest: -1,
    edited: -1,
  };
  [
    ...anchors,
    endAnchor,
  ].forEach((anchor) => {
    const ranges: Record<TThreeWayVersion, IIndexRange> = {
      original: {
        start: previousAnchor.original + 1,
        end: anchor.original,
      },
      latest: {
        start: previousAnchor.latest + 1,
        end: anchor.latest,
      },
      edited: {
        start: previousAnchor.edited + 1,
        end: anchor.edited,
      },
    };
    const hasItems = THREE_WAY_VERSIONS.some((version) => ranges[version].end > ranges[version].start);
    if (hasItems) {
      merged.push(...mergeListSegment(location, arrays, ranges, context));
    }
    if (anchor !== endAnchor) {
      merged.push(arrays.edited[anchor.edited]);
    }
    previousAnchor = anchor;
  });
  return toPresentEntry(merged);
}

/** Returns `undefined` when the value has to be compared as a whole. */
function mergeStructured(location: INodeLocation, entries: IThreeWayVersions<TDiffEntry>, context: IMergeContext): TDiffEntry | undefined {
  const presentValues = getPresentValues(entries);
  if (!presentValues || context.options.isAtomic?.(location.path, presentValues)) {
    return undefined;
  }
  const {
    original, latest, edited,
  } = presentValues;
  if (isPlainObject(original) && isPlainObject(latest) && isPlainObject(edited)) {
    return mergeObject(location, {
      original,
      latest,
      edited,
    }, context);
  }
  if (Array.isArray(original) && Array.isArray(latest) && Array.isArray(edited)) {
    const arrays = {
      original,
      latest,
      edited,
    };
    const getItemKey = context.options.getArrayItemKey?.(location.path);
    if (getItemKey) {
      return mergeKeyedArray(location, arrays, getItemKey, context);
    }
    if (context.options.matchArrayItemsByIndex?.(location.path)) {
      return mergeIndexedArray(location, arrays, context);
    }
    return mergeList(location, arrays, context);
  }
  return undefined;
}

function mergeNode(location: INodeLocation, entries: IThreeWayVersions<TDiffEntry>, context: IMergeContext): TDiffEntry {
  const {
    path,
  } = location;
  const {
    options,
  } = context;
  if (options.ignorePath?.(path)) {
    return entries.edited;
  }
  const isUnchangedInLatest = areEntriesEqual(path, entries.original, entries.latest, options);
  const isEditedLikeLatest = areEntriesEqual(path, entries.latest, entries.edited, options);
  if (isUnchangedInLatest || isEditedLikeLatest) {
    return entries.edited;
  }

  const mergedStructure = mergeStructured(location, entries, context);
  if (mergedStructure) {
    return mergedStructure;
  }
  const choice = resolveRow({
    id: JSON.stringify(path),
    path,
    versionPaths: location.versionPaths,
    kind: getRowKind(path, entries, options),
    ...entries,
  }, context);
  if (choice === 'combined') {
    throw new Error(`The versions of ${JSON.stringify(path)} cannot be combined.`);
  }
  return entries[choice];
}

/**
 * Compares the value at edit start, the latest value and the edited value and merges them.
 *
 * Every value changed in the latest version while the user was editing becomes a row; values changed
 * only in the edited version are kept as edited without a row, and so are values that both versions
 * changed to the same result. No row is an ancestor of another row. Lists without keys are merged
 * like text: insertions, removals and changes in different places are combined, and a place that
 * both versions changed differently becomes a single row with a {@link IThreeWaySegment}.
 */
export function computeThreeWayMerge<T>(
  versions: IThreeWayVersions<T>,
  options: IThreeWayDiffOptions = {},
  choices: TThreeWayChoices = {},
): IThreeWayMergeResult<T> {
  const context: IMergeContext = {
    options,
    choices,
    rows: [],
    unresolvedRowIds: [],
  };
  const rootPath: TDiffPath = [];
  const mergedEntry = mergeNode({
    path: rootPath,
    versionPaths: {
      original: rootPath,
      latest: rootPath,
      edited: rootPath,
    },
  }, {
    original: toPresentEntry(versions.original),
    latest: toPresentEntry(versions.latest),
    edited: toPresentEntry(versions.edited),
  }, context);
  return {
    rows: context.rows,
    merged: clonePlainData(mergedEntry.isPresent ? mergedEntry.value : undefined) as T,
    unresolvedRowIds: context.unresolvedRowIds,
  };
}

export function computeThreeWayDiff<T>(versions: IThreeWayVersions<T>, options: IThreeWayDiffOptions = {}): IThreeWayDiffRow[] {
  return computeThreeWayMerge(versions, options).rows;
}

/**
 * Keeps the latest value where only it changed. Values that both versions changed have no default;
 * the user has to choose.
 */
export function getDefaultThreeWayChoice(row: IThreeWayDiffRow): TThreeWayChoice | undefined {
  let exhaustiveCheck: never;
  switch (row.kind) {
    case 'changedInLatest':
      return 'latest';
    case 'changedInBoth':
      return undefined;
    default:
      exhaustiveCheck = row.kind;
      throw new Error(`Unhandled row kind ${exhaustiveCheck}.`);
  }
}

export function getDefaultThreeWayChoices(rows: IThreeWayDiffRow[]): TThreeWayChoices {
  return Object.fromEntries(rows.map((row) => [
    row.id,
    getDefaultThreeWayChoice(row),
  ]));
}

/**
 * Builds the value to save: the edited value, with the chosen version applied to every row. Pass
 * the options used for the diff. Throws when a row that changed in both versions has no choice.
 */
export function applyThreeWayChoices<T>(versions: IThreeWayVersions<T>, choices: TThreeWayChoices, options: IThreeWayDiffOptions = {}): T {
  const result = computeThreeWayMerge(versions, options, choices);
  if (result.unresolvedRowIds.length > 0) {
    throw new Error(`No version chosen for ${result.unresolvedRowIds.join(', ')}.`);
  }
  return result.merged;
}

/**
 * Reads the value at a path of property names and indices, e.g. a version path of a row. Pass the
 * options of the diff to read a path with item keys instead.
 */
export function getValueAtDiffPath(root: unknown, path: TDiffPath, options: IThreeWayDiffOptions = {}): unknown {
  let currentValue = root;
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (Array.isArray(currentValue)) {
      const getItemKey = options.getArrayItemKey?.(path.slice(0, i));
      currentValue = getItemKey
        ? currentValue.find((item) => getItemKey(item) === segment)
        : currentValue[segment as number];
    } else if (isPlainObject(currentValue)) {
      currentValue = currentValue[String(segment)];
    } else {
      return undefined;
    }
  }
  return currentValue;
}

export function matchesPathPattern(path: TDiffPath, pattern: TDiffPath): boolean {
  return path.length === pattern.length
    && pattern.every((patternSegment, index) => patternSegment === ANY_PATH_SEGMENT || patternSegment === path[index]);
}
